import * as React from "react";
import { Button, Input } from "@jhool-io/fe-components";
import { AxiosError } from "axios";
import { signInWithCustomToken } from "firebase/auth";
import qrcode from "qrcode-generator";
import { auth } from "../../../firebase";
import useToast from "../../../hooks/useToast";
import { IApiResponse } from "../../../utils/types/api-response";
import AuthWrapper from "../components/AuthWrapper/AuthWrapper";
import {
    completeInternalMfaEnrollment,
    startInternalMfaEnrollment,
    verifyInternalMfa,
} from "../services/mfa.services";
import { InternalMfaChallengeMode } from "../utils/mfa";

interface MfaEnrollmentProps {
    mode: Exclude<InternalMfaChallengeMode, "none">;
    onSignOut: () => Promise<void>;
    onVerified: () => Promise<void>;
}

function getErrorMessage(error: unknown, fallback: string): string {
    if (!(error instanceof AxiosError)) {
        return fallback;
    }

    const data = error.response?.data as
        | IApiResponse<unknown>
        | { message?: string }
        | undefined;
    return data?.message ?? fallback;
}

export default function MfaEnrollment({
    mode,
    onSignOut,
    onVerified,
}: MfaEnrollmentProps) {
    const [manualKey, setManualKey] = React.useState("");
    const [qrCode, setQrCode] = React.useState("");
    const [code, setCode] = React.useState("");
    const [isBusy, setIsBusy] = React.useState(false);
    const { toast } = useToast();

    const beginEnrollment = async () => {
        setIsBusy(true);
        try {
            const enrollment = await startInternalMfaEnrollment();
            const generatedQrCode = qrcode(0, "M");
            generatedQrCode.addData(enrollment.otpauth_uri);
            generatedQrCode.make();
            setManualKey(enrollment.manual_key);
            setQrCode(generatedQrCode.createDataURL(5, 4));
        } catch (error) {
            toast({
                mode: "error",
                message: getErrorMessage(
                    error,
                    "Could not start authenticator setup. Please try again."
                ),
            });
        } finally {
            setIsBusy(false);
        }
    };

    const submitCode = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!/^\d{6}$/.test(code)) {
            toast({
                mode: "error",
                message: "Enter the 6-digit code from your authenticator app.",
            });
            return;
        }

        setIsBusy(true);
        try {
            const verification =
                mode === "enrollment"
                    ? await completeInternalMfaEnrollment(code)
                    : await verifyInternalMfa(code);
            await signInWithCustomToken(auth, verification.custom_token);
            await onVerified();
        } catch (error) {
            setCode("");
            toast({
                mode: "error",
                message: getErrorMessage(
                    error,
                    "That code could not be verified. Try a new code."
                ),
            });
        } finally {
            setIsBusy(false);
        }
    };

    const isEnrollmentStarted = mode === "enrollment" && Boolean(manualKey);
    const shouldShowCode = mode === "verification" || isEnrollmentStarted;

    return (
        <AuthWrapper
            footer={
                <Button
                    className="w-full"
                    disabled={isBusy}
                    onClick={onSignOut}
                    variant="secondary"
                >
                    Sign out
                </Button>
            }
        >
            <div>
                <h1 className="text-titleLg font-bold text-text mb-[--spacing-items]">
                    {mode === "enrollment"
                        ? "Secure your account"
                        : "Enter your authenticator code"}
                </h1>
                {mode === "enrollment" && !isEnrollmentStarted && (
                    <div className="space-y-[--spacing-form-items]">
                        <p>
                            Use an authenticator app to add an extra layer of
                            security to your Mantle account.
                        </p>
                        <Button
                            className="w-full"
                            disabled={isBusy}
                            onClick={beginEnrollment}
                        >
                            Set up authenticator
                        </Button>
                    </div>
                )}
                {shouldShowCode && (
                    <form
                        onSubmit={submitCode}
                        className="space-y-[--spacing-form-items]"
                    >
                        {mode === "enrollment" ? (
                            <>
                                <p>
                                    Scan this QR code with your authenticator
                                    app.
                                </p>
                                <img
                                    src={qrCode}
                                    alt="Authenticator setup QR code"
                                    className="mx-auto size-[220px]"
                                />
                                <div>
                                    <p className="text-sm mb-1">
                                        Manual setup code
                                    </p>
                                    <p className="font-mono break-all rounded bg-surface p-3">
                                        {manualKey}
                                    </p>
                                </div>
                            </>
                        ) : (
                            <p>
                                Open your authenticator app and enter the
                                current 6-digit code.
                            </p>
                        )}
                        <Input
                            label="Authenticator code"
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            value={code}
                            onChange={(event) =>
                                setCode(
                                    event.target.value
                                        .replace(/\D/g, "")
                                        .slice(0, 6)
                                )
                            }
                            placeholder="000000"
                        />
                        <Button
                            className="w-full"
                            disabled={isBusy || code.length !== 6}
                            type="submit"
                        >
                            {mode === "enrollment"
                                ? "Confirm authenticator"
                                : "Verify and continue"}
                        </Button>
                    </form>
                )}
            </div>
        </AuthWrapper>
    );
}
